import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, MessageSquare, CalendarCheck, ShieldCheck, X } from "lucide-react";
import { initLenis } from "../lib/smooth";
import { getSiteSettings, digitsOnly } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/ContactFooter";
import { LeadModal } from "../components/LeadForm";
import { StickyMobileBar, WhatsAppFloat } from "../components/StickyElements";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal, SectionHeading } from "../components/Reveal";
import { IMAGES, AGENT_REG, FLOOR_PLANS, WA_MESSAGE } from "../data/site";

const CATEGORY_TITLES = {
  "2 BHK": "Vinayak 21 Acres 2 BHK Floor Plans",
  "3 BHK Smart": "Vinayak 21 Acres 3 BHK Smart Floor Plans",
  "3 BHK Comfort": "Vinayak 21 Acres 3 BHK Comfort Floor Plans",
  "3 BHK Luxury": "Vinayak 21 Acres 3 BHK Luxury Floor Plans",
};

const CATEGORY_INTROS = {
  "2 BHK": "The 2 BHK 2T layout pairs two bedrooms with an efficient living-dining space and balcony — approximately 595–597 sq ft carpet area, available as Type B and Type G across Towers 1A, 1B, 2A and 2B.",
  "3 BHK Smart": "The Smart family is the most compact 3 BHK — efficient 2-toilet layouts of about 698–703 sq ft carpet area, designed to keep every square foot usable.",
  "3 BHK Comfort": "Comfort layouts step up to roughly 736–783 sq ft carpet area with more generous rooms, in 2-toilet and 3-toilet variants depending on tower and type.",
  "3 BHK Luxury": "Luxury layouts are the largest residences — about 823–943 sq ft carpet area with 3 toilets and expansive double-height sky terraces, with balcony, BUA and SBUA varying by odd/even floor.",
};

const FP_FAQS = [
  {
    q: "What floor plans are available at Vinayak 21 Acres?",
    a: "The project offers 2 BHK 2T homes plus 3 BHK homes in Smart, Comfort and Luxury families — 40 unit layouts in total across Towers 1A, 1B, 2A, 2B and 2C (Types A–H). Each layout's official drawing can be viewed on this page.",
  },
  {
    q: "What is the Vinayak 21 Acres 2 BHK floor plan?",
    a: "The 2 BHK 2T offers approximately 595–597 sq ft carpet area with about 60 sq ft balcony, 728 sq ft BUA and 971 sq ft SBUA, depending on tower and type (Type B or Type G in Towers 1A, 1B, 2A and 2B).",
  },
  {
    q: "What 3 BHK configurations are available?",
    a: "Three families: Smart (about 698–703 sq ft carpet), Comfort (about 736–783 sq ft carpet) and Luxury (about 823–943 sq ft carpet). Each is offered in specific tower and type combinations shown in the sections above.",
  },
  {
    q: "What is the difference between Smart, Comfort and Luxury 3 BHK layouts?",
    a: "Smart is the most efficient 3 BHK (2 toilets, ~698–703 sq ft carpet). Comfort adds room size (~736–783 sq ft carpet, 2 or 3 toilets). Luxury is the largest (~823–943 sq ft carpet, 3 toilets) with the biggest double-height sky terraces — balcony, BUA and SBUA vary by odd and even floors.",
  },
  {
    q: "Do apartment sizes vary between towers?",
    a: "Yes, slightly. For example, the 2 BHK 2T carpet area is 596 sq ft in Type B and 595–597 sq ft across Type G variants, while 3 BHK balcony, BUA and SBUA figures change between odd and even floors because of the sky terraces. Always verify the exact figures for your chosen unit.",
  },
  {
    q: "How can I get the latest floor plan?",
    a: "Every official floor plan drawing is available to view on this page. For the detailed sheet for a specific unit, contact Forest Hill Realtors through the enquiry buttons or WhatsApp.",
  },
  {
    q: "How can I book a site visit?",
    a: "Use any Book Site Visit button on this page, the sticky enquiry bar on mobile, or message us on WhatsApp — Forest Hill Realtors will arrange a convenient slot.",
  },
];

export default function FloorPlanPage() {
  const [modal, setModal] = useState({ open: false, source: "Floor Plan Enquiry", interest: "" });
  const [planView, setPlanView] = useState(null);
  const [contact, setContact] = useState({ phone: "+919163841785", whatsapp: "+919163841785" });

  useEffect(() => {
    document.title = "Vinayak 21 Acres Floor Plan | 2 & 3 BHK Layouts Kolkata";
    const meta = document.querySelector('meta[name="description"]');
    if (meta)
      meta.setAttribute(
        "content",
        "Explore Vinayak 21 Acres floor plans for 2 & 3 BHK residences in Kolkata, including Smart, Comfort and Luxury layouts, apartment sizes and availability."
      );
    initLenis();
    getSiteSettings().then(setContact);
  }, []);

  const openModal = (source, interest = "") => setModal({ open: true, source, interest });
  const waLink = `https://wa.me/${digitsOnly(contact.whatsapp)}?text=${encodeURIComponent(WA_MESSAGE)}`;

  return (
    <div className="bg-[#FFFFFF]" data-testid="floorplan-page">
      <Header onBookVisit={() => openModal("Site Visit Booking")} phone={contact.phone} />
      <main>
        <section className="relative bg-[#EAF5D6] pt-36 pb-16 sm:pt-44 sm:pb-20 overflow-hidden hero-grain">
          <div className="absolute inset-0 opacity-[0.14]" aria-hidden="true">
            <img src={IMAGES.hero} alt="" className="w-full h-full object-cover" loading="eager" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="overline-tag" data-testid="fp-overline">Layouts & Plans · Forest Hill Realtors</p>
            <h1
              className="font-serif-display text-[#3F6212] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 max-w-3xl"
              data-testid="fp-h1"
            >
              Vinayak 21 Acres <span className="text-[#84CC16]">Floor Plan</span>
            </h1>
            <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg max-w-2xl leading-relaxed">
              Vinayak 21 Acres offers multiple 2 BHK and 3 BHK layouts across five towers, with configurations varying
              by tower and unit type. Browse every official floor plan below — each with its carpet area, balcony, BUA
              and SBUA as listed in the project brochure.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/" data-testid="fp-home-link"
                className="inline-flex items-center gap-2 text-[#84CC16] font-sub font-semibold text-sm border border-[#84CC16]/50 rounded-full px-6 py-3 hover:bg-[#84CC16]/10 active:scale-[0.98] transition-all duration-300">
                Explore Vinayak 21 Acres <ArrowRight size={15} />
              </Link>
              <Link to="/vinayak-21-acres-price" data-testid="fp-price-link"
                className="inline-flex items-center gap-2 text-[#3F6212] font-sub font-semibold text-sm border border-[#3F6212]/30 rounded-full px-6 py-3 hover:bg-[#3F6212]/10 active:scale-[0.98] transition-all duration-300">
                Check Vinayak 21 Acres Price <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </section>

        {Object.keys(CATEGORY_TITLES).map((cat, ci) => {
          const plans = FLOOR_PLANS.filter((p) => p.category === cat);
          return (
            <section key={cat} data-testid={`fp-section-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              className={`py-16 sm:py-24 ${ci % 2 === 1 ? "bg-[#F4FAE8]" : ""}`}>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                  <SectionHeading index={`0${ci + 1}`} eyebrow={cat} title={CATEGORY_TITLES[cat]} />
                  <Reveal delay={0.1}>
                    <button data-testid={`fp-visit-btn-${ci}`} onClick={() => openModal("Site Visit Booking")}
                      className="inline-flex items-center gap-2 border border-[#9CCB58] text-[#365314] font-sub font-semibold text-[13px] px-6 py-3 rounded-full hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300 shrink-0">
                      <CalendarCheck size={15} /> Book Site Visit
                    </button>
                  </Reveal>
                </div>
                <Reveal delay={0.1}>
                  <p className="text-[#4D7C0F] text-sm sm:text-base leading-relaxed max-w-3xl mt-6">{CATEGORY_INTROS[cat]}</p>
                </Reveal>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-10">
                  {plans.map((p, i) => (
                    <Reveal key={p.slug} delay={0.04 * (i % 4)}>
                      <div data-testid={`fp-card-${p.slug}`}
                        className="bg-white border border-[#DCECC0] rounded-2xl p-6 h-full flex flex-col hover:border-[#84CC16] hover:shadow-[0_20px_40px_-18px_rgba(77,124,15,0.22)] transition-all duration-500">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-serif-display text-xl text-[#365314] leading-snug">{p.config}</h3>
                          <span className="font-mono-spec text-[10px] tracking-[0.15em] uppercase text-[#84CC16] shrink-0">{p.tower}</span>
                        </div>
                        <div className="mt-4 divide-y divide-[#E3EFCB] text-[12px]">
                          {[["Carpet Area", p.carpet], ["Balcony Area", p.balcony], ["BUA", p.bua], ["SBUA", p.sbua]].map(([label, value]) => (
                            <div key={label} className="flex items-center justify-between py-2">
                              <span className="text-[#65A30D]">{label}</span>
                              <span className="font-mono-spec text-[#365314]">{value}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2 mt-5 pt-1">
                          <button data-testid={`fp-view-${p.slug}`} onClick={() => setPlanView(p)}
                            className="flex-1 text-[12px] font-sub font-medium border border-[#9CCB58] text-[#365314] rounded-full px-3 py-2.5 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                            View Floor Plan
                          </button>
                          <button data-testid={`fp-check-${p.slug}`}
                            onClick={() => openModal(`Availability: ${p.config} · ${p.tower}`, p.category.startsWith("2") ? "2 BHK" : "3 BHK")}
                            className="flex-1 text-[12px] font-sub font-bold bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] rounded-full px-3 py-2.5 hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                            Check Availability
                          </button>
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            </section>
          );
        })}

        <section data-testid="fp-variance-section" className="py-14 sm:py-20 bg-[#EAF5D6]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Reveal>
              <p className="overline-tag">Please Note</p>
              <p className="mt-6 text-[#4D7C0F] text-sm sm:text-base leading-relaxed" data-testid="fp-variance-note">
                Apartment areas and layouts may vary according to tower, type and floor. Buyers should verify the
                applicable floor plan, dimensions and current availability before booking.
              </p>
            </Reveal>
          </div>
        </section>

        <section data-testid="fp-faq-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="05" eyebrow="FAQs" title="Floor Plan FAQs" center />
            <Reveal delay={0.15}>
              <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="fp-faq-accordion">
                {FP_FAQS.map((f, i) => (
                  <AccordionItem key={f.q} value={f.q} data-testid={`fp-faq-item-${i}`}
                    className="bg-white border border-[#DCECC0] rounded-xl px-6 data-[state=open]:border-[#84CC16] transition-colors duration-300">
                    <AccordionTrigger data-testid={`fp-faq-trigger-${i}`}
                      className="text-left font-sub font-medium text-[15px] text-[#365314] hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-sm text-[#4D7C0F] leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>

        <section data-testid="fp-cta-section" className="pb-16 sm:pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl bg-[#EAF5D6] p-8 sm:p-12 text-center hero-grain">
                <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3F6212] leading-tight">
                  See the layouts in person
                </h2>
                <p className="text-[#4D7C0F] text-sm sm:text-base mt-4 max-w-xl mx-auto leading-relaxed">
                  Walk through the actual towers, feel the light and ventilation, and pick the floor plan that fits
                  your family best.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center max-w-xl mx-auto">
                  <a data-testid="fp-whatsapp-btn" href={waLink} target="_blank" rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-sub font-bold text-sm px-7 py-4 rounded-full hover:bg-[#1EBE57] active:scale-[0.98] transition-all duration-300">
                    <MessageSquare size={16} /> WhatsApp Us
                  </a>
                  <button data-testid="fp-book-visit-btn" onClick={() => openModal("Site Visit Booking")}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    <CalendarCheck size={16} /> Book a Site Visit
                  </button>
                </div>
                <div className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-2">
                  <Link to="/" data-testid="fp-home-link-bottom" className="inline-flex items-center gap-2 text-[#84CC16] text-sm font-sub font-medium hover:underline">
                    Explore Vinayak 21 Acres <ArrowRight size={14} />
                  </Link>
                  <Link to="/vinayak-21-acres-price" data-testid="fp-price-link-bottom" className="inline-flex items-center gap-2 text-[#84CC16] text-sm font-sub font-medium hover:underline">
                    Check Vinayak 21 Acres Price <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <div className="bg-[#EAF5D6] border-t border-[#DCECC0] py-7" data-testid="fp-partner-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <ShieldCheck size={17} className="text-[#84CC16] shrink-0" />
            <p className="text-[#4D7C0F] text-xs sm:text-[13px] leading-relaxed">
              <span className="text-[#3F6212] font-sub font-semibold">Forest Hill Realtors</span> · Authorised Channel
              Partner · WBRERA Agent Registration: <span className="font-mono-spec">{AGENT_REG}</span>
            </p>
          </div>
        </div>

        <Footer />
      </main>

      <AnimatePresence>
        {planView && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#F4FAE8]/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-8"
            onClick={() => setPlanView(null)} data-testid="fp-plan-viewer-overlay">
            <motion.div initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[92svh] overflow-y-auto p-4 sm:p-6"
              data-testid="fp-plan-viewer-modal">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="overline-tag">{planView.tower}</p>
                  <h3 className="font-serif-display text-2xl text-[#365314] mt-2">{planView.config}</h3>
                </div>
                <button data-testid="fp-plan-viewer-close-btn" onClick={() => setPlanView(null)}
                  className="p-2 text-[#65A30D] hover:text-[#365314] transition-colors" aria-label="Close">
                  <X size={22} />
                </button>
              </div>
              <img src={planView.planImage} alt={`Official floor plan — ${planView.config}, ${planView.tower}, Vinayak 21 Acres`}
                className="w-full h-auto rounded-lg border border-[#DCECC0]" />
              <p className="text-[11px] text-[#65A30D] mt-3 leading-relaxed">
                {planView.note} Plan from the official project brochure; subject to change — verify with Forest Hill Realtors.
              </p>
              <button data-testid="fp-plan-viewer-enquire-btn"
                onClick={() => { const s = `Floor Plan Enquiry: ${planView.config} · ${planView.tower}`; setPlanView(null); openModal(s, planView.category.startsWith("2") ? "2 BHK" : "3 BHK"); }}
                className="mt-5 w-full bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-6 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                Enquire About This Plan
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <LeadModal open={modal.open} onClose={() => setModal((m) => ({ ...m, open: false }))} source={modal.source} defaultInterest={modal.interest} whatsapp={contact.whatsapp} />
      <StickyMobileBar phone={contact.phone} whatsapp={contact.whatsapp} onEnquire={() => openModal("Quick Enquiry")} />
      <WhatsAppFloat whatsapp={contact.whatsapp} />
    </div>
  );
}
